import React from 'react';

// Renders a single Form Flow question by type. Shared by the public renderer
// (PublicFlow) and the builder's live preview. `onAdvance(value)` is called by
// auto-advancing types (single_select / yes_no / rating) with the chosen value.
const norm = (opts) => (opts || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));

const inputBase = "w-full px-4 py-3 text-base border rounded-xl bg-white focus:outline-none focus:ring-2 transition-colors min-h-[52px]";

// PRD 188-189 — short list of dialling codes for the phone question. "Other"
// lets the respondent type their own prefix. The stored value is the code +
// space + number (e.g. "+44 7911 123456") so it still flows to the same key.
const PHONE_CODES = ['+1', '+44', '+61', '+971', '+65', '+91', '+49', '+33', '+27', '+234'];
const PHONE_CODE_LABELS = {
    '+1': '+1 US/CA', '+44': '+44 UK', '+61': '+61 AU', '+971': '+971 UAE', '+65': '+65 SG',
    '+91': '+91 IN', '+49': '+49 DE', '+33': '+33 FR', '+27': '+27 ZA', '+234': '+234 NG',
};

// Splits a stored "+CC rest" value into [code, rest]. Falls back to the default
// code with the whole string as the number when no known prefix is present.
function splitPhone(value) {
    const str = (value ?? '').toString().trimStart();
    // Longest-prefix match so "+1" doesn't shadow nothing and "+971" wins over "+9..".
    const match = [...PHONE_CODES].sort((a, b) => b.length - a.length).find(c => str.startsWith(c + ' ') || str === c);
    if (match) return [match, str.slice(match.length).trimStart()];
    return ['+1', str];
}

function PhoneInput({ value, onChange, inputCls, placeholder }) {
    const [code, rest] = splitPhone(value);
    const isOther = !PHONE_CODES.includes(code);
    const [otherMode, setOtherMode] = React.useState(isOther);
    const [customCode, setCustomCode] = React.useState(isOther ? code : '');

    const compose = (c, r) => `${c} ${r}`.trim();
    const activeCode = otherMode ? (customCode || '') : code;

    const onCodeSelect = (e) => {
        const v = e.target.value;
        if (v === '__other__') {
            setOtherMode(true);
            onChange(compose(customCode, rest));
        } else {
            setOtherMode(false);
            onChange(compose(v, rest));
        }
    };

    return (
        <div className="flex gap-2">
            <select
                value={otherMode ? '__other__' : code}
                onChange={onCodeSelect}
                className="px-2 py-3 text-base border border-stone-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-stone-400 min-h-[52px] shrink-0"
                aria-label="Country code">
                {PHONE_CODES.map(c => <option key={c} value={c}>{PHONE_CODE_LABELS[c]}</option>)}
                <option value="__other__">Other…</option>
            </select>
            {otherMode && (
                <input type="tel" value={customCode}
                    onChange={e => { setCustomCode(e.target.value); onChange(compose(e.target.value, rest)); }}
                    placeholder="+__"
                    className="w-20 px-2 py-3 text-base border border-stone-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-stone-400 min-h-[52px] shrink-0" />
            )}
            <input type="tel" inputMode="tel" value={rest}
                onChange={e => onChange(compose(activeCode, e.target.value))}
                placeholder={placeholder} className={inputCls} />
        </div>
    );
}

export default function FlowScreen({ question, value, onChange, onAdvance, brandColor = '#1c1917', buttonColor = '#1c1917', error }) {
    const q = question || {};
    const type = q.type || 'short_text';
    const inputCls = `${inputBase} ${error ? 'border-red-300 focus:ring-red-300' : 'border-stone-200 focus:ring-stone-400'}`;

    const bigBtn = (selected) => ({
        borderColor: selected ? brandColor : undefined,
        boxShadow: selected ? `0 0 0 1px ${brandColor}` : undefined,
    });

    if (type === 'single_select' || type === 'dropdown') {
        const opts = norm(q.options);
        if (type === 'dropdown') {
            return (
                <select className={inputCls} value={value ?? ''} onChange={e => onChange(e.target.value)}>
                    <option value="">{q.placeholder || 'Select…'}</option>
                    {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            );
        }
        return (
            <div className="space-y-2.5">
                {opts.map(o => (
                    <button key={o.value} type="button"
                        onClick={() => { onChange(o.value); onAdvance && onAdvance(o.value); }}
                        className="w-full text-left px-4 py-3.5 rounded-xl border border-stone-200 bg-white hover:border-stone-400 text-base min-h-[52px] transition-colors"
                        style={bigBtn(value === o.value)}>
                        {o.label}
                    </button>
                ))}
            </div>
        );
    }

    if (type === 'yes_no') {
        const yes = q.config?.yes_label || 'Yes';
        const no = q.config?.no_label || 'No';
        return (
            <div className="grid grid-cols-2 gap-3">
                {[{ v: true, l: yes }, { v: false, l: no }].map(o => (
                    <button key={o.l} type="button"
                        onClick={() => { onChange(o.v); onAdvance && onAdvance(o.v); }}
                        className="px-4 py-4 rounded-xl border border-stone-200 bg-white hover:border-stone-400 text-base font-medium min-h-[56px] transition-colors"
                        style={bigBtn(value === o.v)}>
                        {o.l}
                    </button>
                ))}
            </div>
        );
    }

    if (type === 'multi_select') {
        const opts = norm(q.options);
        const sel = Array.isArray(value) ? value : [];
        const toggle = (v) => sel.includes(v) ? onChange(sel.filter(x => x !== v)) : onChange([...sel, v]);
        return (
            <div className="flex flex-wrap gap-2">
                {opts.map(o => (
                    <button key={o.value} type="button" onClick={() => toggle(o.value)}
                        className="px-4 py-2.5 rounded-full border border-stone-200 bg-white hover:border-stone-400 text-sm min-h-[44px] transition-colors"
                        style={bigBtn(sel.includes(o.value))}>
                        {o.label}
                    </button>
                ))}
            </div>
        );
    }

    if (type === 'rating') {
        const max = Number(q.config?.scale_max) || 5;
        return (
            <div className="flex flex-wrap gap-2">
                {Array.from({ length: max }, (_, i) => i + 1).map(n => (
                    <button key={n} type="button"
                        onClick={() => { onChange(n); onAdvance && onAdvance(n); }}
                        className="w-12 h-12 rounded-xl border border-stone-200 bg-white hover:border-stone-400 text-base font-medium transition-colors"
                        style={bigBtn(value === n)}>
                        {n}
                    </button>
                ))}
            </div>
        );
    }

    if (type === 'long_text') {
        return <textarea rows={4} className={inputCls} placeholder={q.placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
    }

    if (type === 'monetary') {
        const cur = q.config?.currency || 'USD';
        return (
            <div className="flex items-center gap-2">
                <span className="text-sm text-stone-500 px-2 py-3 rounded-lg bg-stone-100">{cur}</span>
                <input type="number" inputMode="decimal" className={inputCls} placeholder={q.placeholder || '0'} value={value ?? ''} onChange={e => onChange(e.target.value)} />
            </div>
        );
    }

    if (type === 'phone') {
        return <PhoneInput value={value} onChange={onChange} inputCls={inputCls} placeholder={q.placeholder} />;
    }

    const typeMap = { email: 'email', number: 'number', short_text: 'text' };
    return <input type={typeMap[type] || 'text'} inputMode={type === 'number' ? 'numeric' : undefined}
        className={inputCls} placeholder={q.placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} />;
}
